var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { SceneAsset } from './SceneAsset.js';
import { SceneObject } from '../SceneObject.js';
import { AssetCollection } from '../../AssetCollection.js';
import { SceneAssetCache, SceneAssetCachePurgeMode } from './SceneAssetCache.js';
import { Observable } from '../../Observable.js';
import { uuidv4 } from '../../uuidv4.js';
import { Logger, LoggingCategory } from '../../logging/Logger.js';
function get_cache_url(url, _extension) {
    return url;
}
export class ViewerScene {
    constructor(viewer, babylon_scene) {
        this.objects = new AssetCollection();
        this.object_changed_observable = new Observable();
        this.object_removed_observable = new Observable();
        this._instanced_mesh_metadata_map = new WeakMap();
        this.viewer = viewer;
        this.babylon_scene = babylon_scene;
        this._asset_cache = new SceneAssetCache(this);
    }
    create_object(asset, name, model_transform) {
        const instance = asset === null ? null : (asset instanceof SceneAsset ? asset.create_instance(model_transform) : asset);
        const object = new SceneObject(instance, this.objects.next_id(), this.viewer, this, asset === null);
        if (instance) {
            instance.metadata.owner = object;
        }
        this.objects.add(object);
        object.name = name + ' (id: ' + object.id + ')';
        if (instance !== null) {
            object.ready = instance.asset.get_loaded_promise();
        }
        else {
            object.ready = Promise.resolve();
        }
        object.disposed_observable.add_once(() => {
            this.object_removed_observable.notify(this, object);
        });
        return object;
    }
    delete_object(object, delete_children) {
        object.dispose(delete_children);
    }
    preload_model_and_mark_persistent(url, extension) {
        return __awaiter(this, void 0, void 0, function* () {
            const cache_url = get_cache_url(url, extension);
            const cached_asset = this._asset_cache.get_cached_asset(cache_url);
            if (cached_asset !== null) {
                yield cached_asset.get_loaded_promise();
                return cached_asset;
            }
            else {
                const asset = new SceneAsset(this);
                this._asset_cache.add_to_cache(cache_url, asset, SceneAssetCachePurgeMode.DISABLED);
                yield asset.load_mesh_from_url(url, extension);
                return asset;
            }
        });
    }
    remove_persistent_marking(url, extension) {
        const cache_url = get_cache_url(url, extension);
        const cached_asset = this._asset_cache.get_cached_asset(cache_url);
        if (cached_asset !== null) {
            if (this.viewer.get_config().verbose_warnings) {
                console.warn("Can't remove persistent marking from model, does not exist in cache: " + cache_url);
            }
            return;
        }
        this._asset_cache.set_asset_purge_mode(url, SceneAssetCachePurgeMode.DELAYED);
    }
    has_persistent_marking(url, extension) {
        const purge_mode = this._asset_cache.get_asset_purge_mode(get_cache_url(url, extension));
        return purge_mode !== null && purge_mode === SceneAssetCachePurgeMode.DISABLED;
    }
    unload_model(url, extension) {
        const cache_url = get_cache_url(url, extension);
        const unloaded = this._asset_cache.unload_from_cache(cache_url);
        if (!unloaded && this.viewer.get_config().verbose_warnings) {
            console.warn('Tried to unload url not present in model cache: ' + cache_url);
        }
    }
    load_mesh(meshes) {
        const asset = new SceneAsset(this);
        asset.load_meshes(meshes);
        const object = this.create_object(asset, 'custom meshes', null);
        // To make sure the asset gets disposed, we actually add it to the cache (with a unique id), and let the cache delete it when its out of users.
        // Unfortunately, it's not enough to dispose on the object's dispose observable as it may have been cloned.
        const cache_url = 'custom_mesh: ' + uuidv4();
        this._asset_cache.add_to_cache(cache_url, asset, SceneAssetCachePurgeMode.INSTANT);
        return object;
    }
    load_model(url, extension, model_transform) {
        const cache_url = get_cache_url(url, extension);
        const cached_asset = this._asset_cache.get_cached_asset(cache_url);
        let asset;
        if (cached_asset === null) {
            const new_asset = new SceneAsset(this);
            new_asset.load_mesh_from_url(url, extension);
            this._asset_cache.add_to_cache(cache_url, new_asset, SceneAssetCachePurgeMode.DELAYED);
            asset = new_asset;
        }
        else {
            asset = cached_asset;
        }
        const filename = url.substring(url.lastIndexOf('/') + 1);
        return this.create_object(asset, filename, model_transform !== null && model_transform !== void 0 ? model_transform : null);
    }
    get_metadata_from_babylon_mesh(mesh) {
        var _a;
        return (_a = this._instanced_mesh_metadata_map.get(mesh)) !== null && _a !== void 0 ? _a : null;
    }
    assign_metadata_to_babylon_mesh(mesh, metadata) {
        if (this._instanced_mesh_metadata_map.has(mesh)) {
            Logger.as(LoggingCategory.METADATA).error('Trying to assign metadata to the same babylon mesh multiple times');
            return;
        }
        this._instanced_mesh_metadata_map.set(mesh, metadata);
    }
    mark_dirty() {
        this.viewer.mark_scene_dirty();
    }
    get_objects(object_ids) {
        return object_ids.map(id => { return this.objects.get(id); });
    }
    update_source_mesh_keys() {
        this.objects.forEach((object) => {
            var _a;
            (_a = object._asset_instance) === null || _a === void 0 ? void 0 : _a.prepare_for_render();
        });
    }
    update(dt) {
        this._asset_cache.update_cache(dt);
    }
    dispose() {
        this._asset_cache.dispose();
    }
}
//# sourceMappingURL=ViewerScene.js.map